import React, { useEffect, useMemo, useState } from "react";
import { Button as FlowBiteButton, Modal } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import BuyWaterSteps from "../../components/BuyWaterComponents/BuyWaterSteps";
import BackButton from "../../components/GeneralComponents/BackButton";
import {
  convertAmountToCubicMetres,
  formatAmount,
  getInputClasses,
  routes,
} from "../../helper/misc";
import { NumericFormat } from "react-number-format";
import Button from "../../components/GeneralComponents/Button";
import { useAuth } from "../../helper/auth";
import { toast } from "react-hot-toast";
import payments from "../../api/payments";
import { usePurchases } from "../../helper/purchases";
import { onAPIError } from "../../helper/functions";
import { cubicMetres } from "../../constants";

function Pay() {
  const { activeMeterOwnership, meterOwnerships } = useAuth();
  const purchases = usePurchases();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState({ loading: false, data: null });
  const [charges, setCharges] = useState({ loading: false, data: null });
  const [waterPrice, setWaterPrice] = useState({ data: null, loading: true });
  const [showChangesModal, setShowChangesModal] = useState(false);

  const amount = purchases.purchaseDetails?.amount;

  useEffect(() => {
    localStorage.setItem("LPA", amount);
  }, [amount]);

  const totalAmount = useMemo(() => {
    return (charges?.data || 0) + (amount || 0);
  }, [charges, amount]);

  const getWaterPrice = async () => {
    try {
      const { data } = await payments.getWaterPrice(activeMeterOwnership.id);
      setWaterPrice({ data: data.data.price });
    } catch (e) {
      setWaterPrice({ loading: false, data: null, error: true });
      onAPIError(e);
    }
  };

  const fetchCharge = async () => {
    setCharges({ loading: true });
    try {
      const { data } = await payments.getCharges({
        amount,
        meter_ownership_id: activeMeterOwnership.id,
      });
      setCharges({ data: data.data.data, loading: false });
    } catch (e) {
      toast.error("Failed to fetch associated charges!");
      window.location.reload();
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handlePayment = () => {
    toast.success("Please do not reload the page....", { duration: 10000 });
    window.location.href = linkData?.data?.subdata?.authorization_url;
  };

  useEffect(() => {
    if (!purchases.purchaseDetails?.amount) {
      if (meterOwnerships?.length > 0) {
        navigate(routes.buy_water);
      } else {
        navigate(routes.manage_meters);
      }
      return;
    }
    getWaterPrice();
    fetchCharge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const amountToPurchase = useMemo(
    () => convertAmountToCubicMetres(waterPrice.data, amount),
    [amount, waterPrice.data]
  );

  const makePurchase = async () => {
    setLinkData({ loading: true });
    try {
      const { data } = await payments.makePurchase({
        expected_cubic_metres: amountToPurchase,
        amount,
        charges: charges.data,
        meter_ownership_id: activeMeterOwnership.id,
      });
      setLinkData({ loading: false, data: data.data });
      const { new_changes, subdata } = data.data;
      if (new_changes.water_price) {
        setWaterPrice({ data: new_changes.water_price });
      }
      if (new_changes.water_price || new_changes.charges) {
        setShowChangesModal(true);
      } else {
        toast.success("Please do not reload the page....", { duration: 10000 });
        window.location.href = subdata?.authorization_url;
      }
    } catch (e) {
      onAPIError(e);
      setLinkData({ loading: false });
    }
  };

  return (
    <>
      <div className="pt-5 flex flex-col space-y-[47px]">
        <div className="hidden lg:block">
          <BuyWaterSteps />
        </div>
        <div className="bg-pw-card lg:rounded-[30px] mx-auto max-w-[1004.68px] w-full p-4 lg:mr-[50px] lg:p-[50px]">
          <div className="pb-[47px] flex items-center justify-between !w-full">
            <BackButton onClick={() => navigate(-1)} />
            <h2 className="font-semibold text-center lg:text-start text-2xl md:text-3xl mx-auto ">
              Pay
            </h2>
            <div className="opacity-0 pointer-events-none">
              <BackButton />
            </div>
          </div>
          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-[52.66px] w-full">
            <div className="flex flex-col space-y-1 w-full">
              <input
                value={activeMeterOwnership?.meter?.number}
                disabled
                className={getInputClasses(false, "w-full")}
              />
              <span className="text-sm text-black">
                {activeMeterOwnership?.meter?.tenant?.name}
              </span>
            </div>
            <NumericFormat
              displayType="input"
              thousandSeparator
              disabled
              value={purchases?.purchaseDetails?.amount}
              decimalScale={2}
              className={getInputClasses(false, "w-full self-start")}
            />
          </div>
          <div className="mt-[27px] flex flex-col items-center justify-center">
            <p className="textpwpw-grey2 text-sm font-medium text-center">
              The equivalent Amount Of water you will get in cubic metres
            </p>
            <NumericFormat
              displayType="text"
              thousandSeparator
              decimalScale={2}
              value={amountToPurchase}
              decimalSeparator="."
              fixedDecimalScale
              className="textpw-dark text-[13px] font-semibold text-center"
            />
            <p className="textpwpw-grey2 text-sm font-medium text-center">
              Charges: NGN{formatAmount(charges?.data || 0)}
            </p>

            <div className="mt-[47px] flex items-center justif-center">
              <Button
                text={`Pay NGN ${formatAmount(totalAmount)}`}
                loading={charges.loading || linkData.loading}
                onClick={makePurchase}
                disabled={!linkData || charges.loading || waterPrice.loading}
              />
            </div>
          </div>
        </div>
      </div>
      {/* changes modal */}
      <Modal show={showChangesModal} onClose={() => setShowChangesModal(false)}>
        <Modal.Header>Alert</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
              There have been new changes to the price
            </p>
            <div className="flex flex-col space-y-4">
              {linkData?.data?.new_changes?.water_price && (
                <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                  Amount per 1 {cubicMetres}:{" "}
                  <NumericFormat
                    displayType="text"
                    thousandSeparator
                    decimalScale={2}
                    value={linkData?.data?.new_changes?.water_price}
                    decimalSeparator="."
                    fixedDecimalScale
                    className="textpw-dark text-[13px] font-semibold text-center"
                  />
                </p>
              )}
              {linkData?.data?.new_changes?.charges && (
                <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
                  Charges:{" "}
                  <NumericFormat
                    displayType="text"
                    thousandSeparator
                    decimalScale={2}
                    value={linkData?.data?.new_changes?.charges}
                    decimalSeparator="."
                    fixedDecimalScale
                    className="textpw-dark text-[13px] font-semibold text-center"
                  />
                </p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <FlowBiteButton
            onClick={() => {
              setShowChangesModal(false);
              handlePayment();
            }}
          >
            Proceed
          </FlowBiteButton>
          <FlowBiteButton
            color="gray"
            onClick={() => setShowChangesModal(false)}
          >
            Cancel
          </FlowBiteButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Pay;
